// Mock data for yBro marketing website

export const companyInfo = {
  name: "yBro",
  tagline: "Elevate Your Local Presence",
  email: "hello@ybro.com",
  phone: "+44 20 3996 7644",
  copyright: "© 2026 yBro Agency. All rights reserved."
};

export const heroContent = {
  headline: "Reviews as Growth Engine. Compliance as Shield",
  subheadline: "Expert GBP Management, Review Handling, Local SEO, Ad Campaigns & DMCC Act Compliance",
  description: "In today's digital landscape, your Google Business Profile is your most powerful local asset. yBro provides expert, results‑driven services to optimise visibility, protect reputation, drive qualified traffic through SEO and ads, and ensure full compliance with the UK's DMCC Act on fake reviews. Trusted by SMEs across the UK, US, and India for fast growth and by Enterprises for scalable, multi‑location excellence.",
  primaryCta: "Get Your Free Consultation",
  secondaryCta: "Or call us: +44 20 3996 7644"
};

export const quickBenefits = [
  "Google Business Profile experts for UK, US, and India local markets.",
  "Proactive review response and reputation management.",
  "Local SEO and Google Ads that focus on ROI, not vanity metrics.",
  "DMCC Act fake review risk assessment and compliance support."
];

export const services = [
  {
    id: 1,
    title: "Full Google Business Profile Optimisation",
    shortTitle: "GBP Profile Management",
    description: "Complete management – information updates, high‑quality photos, regular posts and Q&A handling – to rank higher in local search and attract more of the right customers.",
    fullDescription: "Your Google Business Profile is often the first touchpoint for new customers. yBro ensures every element is complete, accurate and optimised – from categories, descriptions and attributes to photos, posts and Q&A. This helps you rank higher in local packs and maps, while giving customers the confidence to choose you.",
    inclusions: [
      "Initial GBP audit and fix of incomplete or inconsistent data.",
      "Ongoing updates to hours, services, attributes and location details.",
      "High‑quality photo and post strategy to keep your profile active and engaging.",
      "Q&A and messaging handling to reduce missed opportunities."
    ],
    icon: "MapPin"
  },
  {
    id: 2,
    title: "Strategic Review Management",
    shortTitle: "Professional Review Response",
    description: "Thoughtful, on‑brand responses to every review that strengthen reputation, boost local SEO signals and turn feedback into loyalty.",
    fullDescription: "Reviews strongly influence both rankings and conversions. yBro designs and runs a compliant, brand‑aligned review management process that encourages genuine feedback and responds to every review in a calm, professional tone.",
    inclusions: [
      "Create review response frameworks aligned to your brand voice and sector.",
      "Respond to positive and negative reviews within agreed timeframes.",
      "Flag potential fake or abusive reviews and advise on takedown routes.",
      "Provide insights from review trends to improve operations and customer experience."
    ],
    icon: "Star"
  },
  {
    id: 3,
    title: "Targeted Local SEO + Google Ads",
    shortTitle: "Local SEO & Ad Campaigns",
    description: "On‑page and off‑page local SEO combined with tightly targeted, high‑conversion Google Ads to drive foot traffic, calls and leads with clear ROI tracking.",
    fullDescription: "We combine technical and content‑driven local SEO with tightly targeted Google Ads campaigns to drive measurable results, not just traffic. The focus is on actions that matter – calls, bookings, direction requests and enquiries.",
    inclusions: [
      "Local on‑page SEO (location pages, metadata, internal linking).",
      "Local citation and listing clean‑up for consistency.",
      "Keyword and competitor analysis for priority areas.",
      "Google Ads setup and optimisation for local intent, including call and location extensions.",
      "Clear reporting that ties spend to leads and revenue."
    ],
    icon: "TrendingUp"
  },
  {
    id: 4,
    title: "DMCC Act Compliance & Risk Audit",
    shortTitle: "DMCC Fake Review Risk Assessment",
    description: "Expert assessment of fake‑review exposure under the UK's Digital Markets, Competition and Consumers Act, with practical steps to reduce legal, financial and reputational risk for UK businesses.",
    fullDescription: "The Digital Markets, Competition and Consumers Act 2024 makes fake and misleading reviews a banned practice in the UK, with significant potential penalties. yBro helps you understand your exposure and put practical controls in place across Google, your website and third‑party platforms for UK businesses.",
    inclusions: [
      "Map where and how your business collects and displays reviews.",
      "Assess current processes against DMCC rules on fake, misleading and incentivised reviews.",
      "Identify high‑risk practices (e.g. unverified review sources, hidden incentives, cherry‑picking positives).",
      "Recommend proportionate policies, staff training and monitoring processes.",
      "Provide ongoing updates as CMA guidance and enforcement evolve."
    ],
    icon: "Shield"
  },
  {
    id: 5,
    title: "Enterprise Reputation & Trust Governance",
    shortTitle: "Enterprise Governance",
    description: "Enterprise‑grade governance for reputation, AI visibility, and internal trust signals.",
    fullDescription: "A structured governance programme for large organisations to manage public reputation, AI visibility accuracy, and internal trust signals. Designed for executive oversight, clear escalation paths, and measurable risk reduction.",
    inclusions: [
      "Reputation and AI visibility risk audit.",
      "Governance playbooks and escalation matrix.",
      "AI visibility accuracy monitoring and reporting.",
      "Executive dashboards and leadership reviews."
    ],
    icon: "Building2"
  }
];

export const whyYBro = {
  title: "Why UK, US, and India Businesses Choose yBro",
  intro: "yBro combines deep local search expertise with a clear, compliance‑first approach so you can scale confidently.",
  reasons: [
    {
      title: "Proven local results",
      description: "Consistent improvements in visibility, calls and direction requests from well‑optimised GBP listings and campaigns."
    },
    {
      title: "UK regulatory expertise",
      description: "Up‑to‑date understanding of the UK's DMCC Act and CMA guidance so your review strategy stays compliant."
    },
    {
      title: "Dedicated support",
      description: "Named account management, responsive communication and proactive optimisation, not 'set and forget' marketing."
    },
    {
      title: "Scalable for any size",
      description: "Processes and tooling built for single‑location SMEs through to multi‑location enterprises with complex structures."
    },
    {
      title: "Transparent and ethical",
      description: "No black‑hat tactics, no fake reviews, full clarity on what we are doing, why and how it impacts your business."
    }
  ]
};

export const aboutContent = {
  title: "About yBro",
  mainText: "At yBro we help businesses across the UK, US, and India dominate local search. From single‑location SMEs to multi‑site enterprises, our team delivers hands‑on GBP optimisation, intelligent review strategies, high‑ROI local SEO and advertising, and specialist DMCC Act risk assessments for UK operations to keep you compliant and protected.",
  secondaryText: "We act as an extension of your marketing and operations teams, blending technical expertise with clear communication so you always know what is happening, what it is costing and what it is delivering.",
  benefits: [
    "Affordable, high‑impact solutions for growing SMEs.",
    "Scalable multi‑location management for enterprises and franchises.",
    "Full alignment with the UK's DMCC Act fake‑review regulations and CMA best‑practice guidance.",
    "Transparent, results‑focused partnership, with clear reporting and honest recommendations."
  ],
  process: [
    { step: "Audit", description: "Review your existing GBP, website, reviews and ad activity." },
    { step: "Strategy", description: "Define local search, review and compliance strategy aligned to your goals." },
    { step: "Execution", description: "Implement optimisation, campaigns and review processes." },
    { step: "Optimise", description: "Iterate based on performance, seasonality and regulatory updates." }
  ]
};

export const dmccContent = {
  pageTitle: "DMCC Act Fake Review Compliance for UK Businesses",
  intro: "The Digital Markets, Competition and Consumers Act 2024 (DMCC Act) introduces specific, banned practices around fake and misleading consumer reviews in the UK. Submitting, commissioning or facilitating fake reviews, publishing reviews in a misleading way, or concealing incentivised reviews can now trigger serious regulatory action, including substantial fines.",
  keyPoints: [
    {
      title: "Fake and misleading reviews are now explicitly banned",
      description: "The DMCC Act makes it an automatically unfair commercial practice to submit or commission fake reviews, or reviews that hide the fact they have been incentivised."
    },
    {
      title: "Misleading presentation is also prohibited",
      description: "Businesses must not publish reviews in a misleading way – for example by cherry‑picking only positive feedback or hiding negative reviews to distort the overall impression."
    },
    {
      title: "Review systems must be proactively managed",
      description: "Traders are expected to take 'reasonable and proportionate steps' to prevent, detect and remove fake or misleading reviews, including improving verification, detection systems and user reporting."
    },
    {
      title: "Non‑compliance carries real consequences",
      description: "The Competition and Markets Authority can impose fines up to the higher of £300,000 or 10% of global annual turnover for serious breaches, and individuals can also face significant penalties in some circumstances."
    }
  ],
  howWeHelp: {
    title: "How yBro helps you stay compliant",
    subtitle: "Operational support, not textbook theory",
    description: "yBro translates the DMCC Act's rules on fake reviews into practical processes that your team can actually follow. The goal is to protect your reputation, reduce regulatory risk and keep your review strategy aligned with genuine customer experience.",
    areas: [
      {
        title: "Policy & process review",
        description: "We help you create clear internal policies banning fake or misleading reviews, and define how any incentives must be disclosed."
      },
      {
        title: "Risk assessment across platforms",
        description: "We map where reviews appear – GBP, your website, third‑party sites – and assess each channel against DMCC expectations."
      },
      {
        title: "Practical controls & monitoring",
        description: "We recommend proportionate verification checks, staff training, detection mechanisms and escalation paths when suspicious reviews are identified."
      },
      {
        title: "Ongoing guidance",
        description: "As CMA guidance and enforcement develop, we help you adjust your approach so it remains robust and defensible."
      }
    ]
  },
  ownPolicy: {
    title: "Our own review policy",
    points: [
      "We never create, buy or sell fake reviews or endorsements for any business.",
      "We do not encourage or assist clients to break the DMCC Act or platform rules on reviews.",
      "If a review has been incentivised, we require that this is clearly disclosed and that the reviewer is a genuine customer.",
      "We support clients in publishing a fair and representative mix of reviews, not just selective positives."
    ]
  },
  disclaimer: "This page is for general information only and is not legal advice. For formal interpretation of the DMCC Act you should consult the official CMA guidance and your legal advisers."
};

export const contactContent = {
  title: "Ready to Boost Your Local Visibility?",
  description: "Contact us today for a no‑obligation chat or a free initial assessment of your Google Business Profile, local search presence and DMCC review risks.",
  serviceOptions: [
    "GBP Management",
    "Review Management",
    "Local SEO & Ads",
    "DMCC Compliance",
    "Enterprise",
    "Partnership",
    "General Enquiry"
  ]
};

export const navigation = [
  { name: "Home", href: "/" },
  { name: "Services", href: "/services" },
  { name: "Use Cases", href: "/use-cases" },
  { name: "Enterprise", href: "/enterprise" },
  { name: "DMCC Compliance", href: "/dmcc-compliance" },
  { name: "Contact", href: "/contact" }
];

export const footerNavigation = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Pricing", href: "/pricing" },
  { name: "Enterprise", href: "/enterprise" },
  { name: "DMCC Compliance", href: "/dmcc-compliance" },
  { name: "Contact", href: "/contact" }
];

export const footerLinks = [
  { name: "Privacy Policy", href: "/privacy" },
  { name: "Terms of Service", href: "/terms" },
  { name: "DMCC Fake Review Compliance", href: "/dmcc-compliance" }
];
